'use client'

import {useEffect, useRef, useState} from 'react'
import {BarChart3, Bot, CheckCircle2, Loader2, Lock, Mail, RefreshCw, ShieldCheck, User, UserPlus, Users, X} from 'lucide-react'
import {apiGet, apiPost, setAuthTokens, setAuthUser} from '@/lib/api'

type LoginResponse = {
    success: boolean
    token: string
    refresh_token: string
    user: { id: number; username: string; role: string }
}

type RegistrationConfig = {
    success: boolean
    enabled: boolean
    trialDays: number
    loginCaptchaEnabled?: boolean
}

type SlideCaptcha = {
    id: string
    masterImage: string
    tileImage: string
    width: number
    height: number
    tileX: number
    tileY: number
    tileWidth: number
    tileHeight: number
    expiresIn: number
}

export function LoginPage({onLogin}: {onLogin: () => void}) {
    const [username, setUsername] = useState('')
    const [password, setPassword] = useState('')
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState('')
    const [registrationConfig, setRegistrationConfig] = useState<RegistrationConfig | null>(null)
    const [showRegister, setShowRegister] = useState(false)
    const [showCaptcha, setShowCaptcha] = useState(false)
    const [captcha, setCaptcha] = useState<SlideCaptcha | null>(null)
    const [captchaLoading, setCaptchaLoading] = useState(false)
    const [captchaVerified, setCaptchaVerified] = useState(false)
    const [dragX, setDragX] = useState(0)
    const dragRef = useRef<{startPointerX: number; startDragX: number} | null>(null)
    const captchaTrackRef = useRef<HTMLDivElement | null>(null)
    const dragXRef = useRef(0)

    useEffect(() => {
        apiGet<RegistrationConfig>('/auth/register/config', {silent: true})
            .then((res) => setRegistrationConfig(res))
            .catch(() => setRegistrationConfig({success: false, enabled: false, trialDays: 0, loginCaptchaEnabled: true}))
    }, [])

    async function refreshCaptcha() {
        setCaptchaLoading(true)
        setCaptchaVerified(false)
        try {
            const res = await apiGet<{success: boolean; captcha: SlideCaptcha}>('/auth/captcha/slide', {silent: true})
            if (res.success && res.captcha) {
                setCaptcha(res.captcha)
                setDragX(res.captcha.tileX)
                dragXRef.current = res.captcha.tileX
            }
        } catch {
            setError('验证码加载失败，请刷新重试')
        } finally {
            setCaptchaLoading(false)
        }
    }

    async function handleSubmit(e: React.FormEvent) {
        e.preventDefault()
        if (!username.trim() || !password.trim()) {
            setError('请输入用户名和密码')
            return
        }
        setError('')
        if (registrationConfig?.loginCaptchaEnabled === false) {
            await handleVerifiedLogin(0, true)
            return
        }
        setShowCaptcha(true)
        void refreshCaptcha()
    }

    async function handleVerifiedLogin(verifiedX = dragX, verified = captchaVerified) {
        const captchaEnabled = registrationConfig?.loginCaptchaEnabled !== false
        if (captchaEnabled && (!captcha || !verified)) {
            setError('请先完成滑动验证')
            return
        }
        setLoading(true)
        setError('')
        const payload: Record<string, unknown> = {
            username: username.trim(),
            password: password.trim(),
        }
        if (captchaEnabled && captcha) {
            payload.captchaId = captcha.id
            payload.captchaX = Math.round(verifiedX)
            payload.captchaY = captcha.tileY
        }
        try {
            const res = await apiPost<LoginResponse>('/auth/login', payload, {silent: true})
            if (res.success && res.token && res.refresh_token) {
                setAuthTokens(res.token, res.refresh_token)
                setAuthUser(res.user)
                onLogin()
            } else {
                setError('登录失败')
                setShowCaptcha(false)
                if (captchaEnabled) void refreshCaptcha()
            }
        } catch (err) {
            setError(err instanceof Error ? err.message : '登录失败')
            setShowCaptcha(false)
            if (captchaEnabled) void refreshCaptcha()
        } finally {
            setLoading(false)
        }
    }

    function closeCaptcha() {
        if (loading) return
        setShowCaptcha(false)
        setCaptchaVerified(false)
        dragRef.current = null
    }

    function handleCaptchaPointerDown(e: React.PointerEvent<HTMLButtonElement>) {
        if (!captcha || loading || captchaLoading) return
        e.currentTarget.setPointerCapture(e.pointerId)
        dragRef.current = {
            startPointerX: e.clientX,
            startDragX: dragX,
        }
    }

    function handleCaptchaPointerMove(e: React.PointerEvent<HTMLButtonElement>) {
        if (!captcha || !dragRef.current) return
        const trackWidth = captchaTrackRef.current?.getBoundingClientRect().width || captcha.width
        const scale = captcha.width / Math.max(1, trackWidth)
        const maxX = captcha.width - captcha.tileWidth
        const nextX = Math.min(maxX, Math.max(0, dragRef.current.startDragX + (e.clientX - dragRef.current.startPointerX) * scale))
        setDragX(nextX)
        dragXRef.current = nextX
        setCaptchaVerified(nextX > captcha.tileX + 12)
    }

    function handleCaptchaPointerUp(e: React.PointerEvent<HTMLButtonElement>) {
        if (!captcha || !dragRef.current) return
        const nextX = dragXRef.current
        const verified = nextX > captcha.tileX + 12
        dragRef.current = null
        e.currentTarget.releasePointerCapture(e.pointerId)
        setCaptchaVerified(verified)
        if (verified) {
            void handleVerifiedLogin(nextX, true)
        }
    }

    return (
        <div className="flex min-h-screen items-center justify-center bg-[#eef2f5] p-4 font-sans text-ink">
            <div className="flex h-auto w-full max-w-5xl overflow-hidden rounded-card border border-white/70 bg-white shadow-2xl shadow-gray-900/10 lg:min-h-[600px]">
                <section className="relative hidden w-1/2 overflow-hidden bg-accent p-12 text-white lg:flex lg:flex-col lg:justify-between">
                    <div className="absolute inset-x-0 bottom-0 h-48 bg-gradient-to-t from-black/10 to-transparent"/>
                    <div className="relative">
                        <div className="mb-9 flex items-center gap-3">
                            <div className="flex h-11 w-11 items-center justify-center rounded-card bg-white/15 ring-1 ring-white/25">
                                <Bot size={24}/>
                            </div>
                            <div>
                                <div className="text-lg font-semibold">微信机器人系统</div>
                                <div className="text-xs text-white/70">Wechat Bot Console</div>
                            </div>
                        </div>

                        <h1 className="text-4xl font-bold leading-tight">
                            智能托管微信运营，
                            <br/>
                            高效连接客户与社群
                        </h1>
                        <p className="mt-6 max-w-md text-sm leading-7 text-white/85">
                            集中管理多账号、自动回复、群成员维护和运营数据，让重复操作交给系统处理，管理员专注在关键沟通上。
                        </p>
                    </div>

                    <div className="relative space-y-5">
                        <FeatureItem icon={<Users size={18}/>} text="支持多微信实例与群聊管理"/>
                        <FeatureItem icon={<Bot size={18}/>} text="自动回复、欢迎语与定时任务统一配置"/>
                        <FeatureItem icon={<BarChart3 size={18}/>} text="实时会话与状态监控，异常更快发现"/>
                    </div>
                </section>

                <section className="flex w-full flex-col justify-center px-6 py-10 sm:px-10 lg:w-1/2 lg:p-12">
                    <div className="mx-auto w-full max-w-sm">
                        <div className="mb-9">
                            <div className="mb-5 flex h-14 w-14 items-center justify-center rounded-card bg-accent/10 text-accent lg:hidden">
                                <Bot size={28}/>
                            </div>
                            <div className="mb-3 inline-flex items-center gap-2 rounded-card bg-green-50 px-3 py-1.5 text-xs font-medium text-accent">
                                <ShieldCheck size={14}/>
                                管理员入口
                            </div>
                            <h2 className="text-3xl font-bold tracking-normal text-ink">欢迎回来</h2>
                            <p className="mt-2 text-sm text-muted">请登录您的管理员账号，进入微信机器人控制台</p>
                        </div>

                        <form onSubmit={handleSubmit} className="space-y-5">
                            <div>
                                <label className="mb-2 block text-sm font-medium text-soft">用户名</label>
                                <div className="relative">
                                    <User size={18} className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-muted"/>
                                    <input
                                        type="text"
                                        value={username}
                                        onChange={(e) => setUsername(e.target.value)}
                                        placeholder="请输入账号"
                                        className="h-12 w-full rounded-card border border-hair bg-white pl-11 pr-4 text-sm text-ink outline-none transition-all placeholder:text-gray-400 focus:border-accent focus:ring-2 focus:ring-accent/15"
                                        autoFocus
                                        autoComplete="username"
                                    />
                                </div>
                            </div>
                            <div>
                                <label className="mb-2 block text-sm font-medium text-soft">密码</label>
                                <div className="relative">
                                    <Lock size={18} className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-muted"/>
                                    <input
                                        type="password"
                                        value={password}
                                        onChange={(e) => setPassword(e.target.value)}
                                        placeholder="请输入密码"
                                        className="h-12 w-full rounded-card border border-hair bg-white pl-11 pr-4 text-sm text-ink outline-none transition-all placeholder:text-gray-400 focus:border-accent focus:ring-2 focus:ring-accent/15"
                                        autoComplete="current-password"
                                    />
                                </div>
                            </div>

                            {error && (
                                <div className="rounded-card border border-red-100 bg-red-50 px-3 py-2.5 text-sm text-red-600">
                                    {error}
                                </div>
                            )}

                            <button
                                type="submit"
                                disabled={loading}
                                className="flex h-12 w-full items-center justify-center gap-2 rounded-card bg-accent text-sm font-semibold text-white shadow-lg shadow-green-500/20 transition-all hover:bg-accent-hover active:scale-[0.99] disabled:cursor-not-allowed disabled:opacity-60"
                            >
                                {loading ? (
                                    <>
                                        <Loader2 size={17} className="animate-spin"/>
                                        登录中...
                                    </>
                                ) : (
                                    '立即登录'
                                )}
                            </button>
                        </form>

                        <div className="mt-7 flex items-center justify-center gap-2 text-sm text-muted">
                            <CheckCircle2 size={16} className="text-accent"/>
                            系统账号由管理员统一开通
                        </div>
                        {registrationConfig?.enabled ? (
                            <button
                                type="button"
                                className="mx-auto mt-4 flex items-center justify-center gap-2 text-sm font-medium text-accent hover:text-accent-hover"
                                onClick={() => setShowRegister(true)}
                            >
                                <UserPlus size={16}/>
                                注册试用账号
                            </button>
                        ) : null}
                    </div>
                </section>
            </div>
            {showRegister ? (
                <RegisterDialog
                    trialDays={registrationConfig?.trialDays || 7}
                    onClose={() => setShowRegister(false)}
                    onRegistered={(nextUsername) => {
                        setUsername(nextUsername)
                        setPassword('')
                        setShowRegister(false)
                    }}
                />
            ) : null}
            {showCaptcha ? (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/35 p-4">
                    <div className="w-full max-w-sm rounded-card bg-white p-5 shadow-2xl shadow-gray-900/20">
                        <div className="mb-4 flex items-center justify-between gap-3">
                            <div>
                                <div className="text-base font-semibold text-ink">安全验证</div>
                                <div className="mt-1 text-xs text-muted">释放滑块后自动验证登录</div>
                            </div>
                            <button
                                type="button"
                                onClick={closeCaptcha}
                                disabled={loading}
                                className="inline-flex h-8 w-8 items-center justify-center rounded-card text-muted transition-colors hover:bg-gray-100 hover:text-ink disabled:opacity-50"
                                aria-label="关闭验证码"
                            >
                                <X size={18}/>
                            </button>
                        </div>
                        <SlideCaptchaPanel
                            captcha={captcha}
                            dragX={dragX}
                            loading={captchaLoading}
                            verified={captchaVerified}
                            trackRef={captchaTrackRef}
                            onRefresh={() => void refreshCaptcha()}
                            onPointerDown={handleCaptchaPointerDown}
                            onPointerMove={handleCaptchaPointerMove}
                            onPointerUp={handleCaptchaPointerUp}
                        />
                        {loading ? (
                            <div className="mt-4 flex h-11 items-center justify-center gap-2 rounded-card bg-accent/10 text-sm font-medium text-accent">
                                <Loader2 size={16} className="animate-spin"/>
                                登录中...
                            </div>
                        ) : null}
                    </div>
                </div>
            ) : null}
        </div>
    )
}

function SlideCaptchaPanel({
    captcha,
    dragX,
    loading,
    verified,
    trackRef,
    onRefresh,
    onPointerDown,
    onPointerMove,
    onPointerUp,
}: {
    captcha: SlideCaptcha | null
    dragX: number
    loading: boolean
    verified: boolean
    trackRef: React.RefObject<HTMLDivElement | null>
    onRefresh: () => void
    onPointerDown: (e: React.PointerEvent<HTMLButtonElement>) => void
    onPointerMove: (e: React.PointerEvent<HTMLButtonElement>) => void
    onPointerUp: (e: React.PointerEvent<HTMLButtonElement>) => void
}) {
    const width = captcha?.width || 300
    const height = captcha?.height || 220
    const tileWidth = captcha?.tileWidth || 60
    const progress = captcha ? Math.min(100, Math.max(0, (dragX / Math.max(1, width - tileWidth)) * 100)) : 0
    const tileLeft = captcha ? `${(dragX / width) * 100}%` : 0
    const tileTop = captcha ? `${(captcha.tileY / height) * 100}%` : 0
    const tileDisplayWidth = captcha ? `${(captcha.tileWidth / width) * 100}%` : 0
    const tileDisplayHeight = captcha ? `${(captcha.tileHeight / height) * 100}%` : 0
    const handleRatio = captcha ? dragX / Math.max(1, width - tileWidth) : 0
    const handleLeft = captcha ? `calc(${handleRatio * 100}% - ${48 * handleRatio}px)` : 0

    return (
        <div className="rounded-card border border-hair bg-gray-50 p-3">
            <div className="mb-2 flex items-center justify-between gap-3">
                <span className="text-xs font-medium text-soft">滑动完成安全验证</span>
                <button
                    type="button"
                    onClick={onRefresh}
                    disabled={loading}
                    className="inline-flex h-7 w-7 items-center justify-center rounded-card text-muted transition-colors hover:bg-white hover:text-accent disabled:opacity-50"
                    title="刷新验证码"
                >
                    {loading ? <Loader2 size={15} className="animate-spin"/> : <RefreshCw size={15}/>}
                </button>
            </div>

            <div className="relative mx-auto w-full overflow-hidden rounded-card border border-hair bg-white" style={{maxWidth: width, aspectRatio: `${width} / ${height}`}}>
                {captcha ? (
                    <>
                        <img src={captcha.masterImage} alt="" draggable={false} className="h-full w-full select-none object-cover"/>
                        <img
                            src={captcha.tileImage}
                            alt=""
                            draggable={false}
                            className="pointer-events-none absolute select-none"
                            style={{
                                left: tileLeft,
                                top: tileTop,
                                width: tileDisplayWidth,
                                height: tileDisplayHeight,
                            }}
                        />
                    </>
                ) : (
                    <div className="flex h-full items-center justify-center text-sm text-muted">
                        {loading ? '验证码加载中...' : '验证码加载失败'}
                    </div>
                )}
            </div>

            <div ref={trackRef} className="relative mx-auto mt-3 h-10 w-full rounded-card border border-hair bg-white" style={{maxWidth: width}}>
                <div className="absolute inset-y-0 left-0 rounded-card bg-accent/10" style={{width: `${progress}%`}}/>
                <div className="absolute inset-0 flex items-center justify-center text-xs text-muted">
                    {verified ? '释放后点击登录' : '向右拖动滑块'}
                </div>
                <button
                    type="button"
                    disabled={!captcha || loading}
                    onPointerDown={onPointerDown}
                    onPointerMove={onPointerMove}
                    onPointerUp={onPointerUp}
                    onPointerCancel={onPointerUp}
                    className="absolute top-0 flex h-10 w-12 touch-none items-center justify-center rounded-card border border-accent/30 bg-white text-accent shadow-sm transition-colors hover:bg-green-50 disabled:cursor-not-allowed disabled:opacity-60"
                    style={{left: handleLeft}}
                    aria-label="拖动滑块完成验证"
                >
                    {verified ? <CheckCircle2 size={18}/> : <ShieldCheck size={18}/>}
                </button>
            </div>
        </div>
    )
}

function FeatureItem({icon, text}: {icon: React.ReactNode; text: string}) {
    return (
        <div className="flex items-center gap-3 text-sm text-white/90">
            <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-white/15 ring-1 ring-white/20">
                {icon}
            </div>
            <span>{text}</span>
        </div>
    )
}

function RegisterDialog({
    trialDays,
    onClose,
    onRegistered,
}: {
    trialDays: number
    onClose: () => void
    onRegistered: (username: string) => void
}) {
    const [username, setUsername] = useState('')
    const [email, setEmail] = useState('')
    const [password, setPassword] = useState('')
    const [code, setCode] = useState('')
    const [status, setStatus] = useState('')
    const [error, setError] = useState('')
    const [sending, setSending] = useState(false)
    const [registering, setRegistering] = useState(false)

    async function checkAccount() {
        const res = await apiGet<{
            success: boolean
            usernameAvailable?: boolean
            usernameMessage?: string
            emailAvailable?: boolean
            emailMessage?: string
        }>(
            `/auth/register/check?username=${encodeURIComponent(username.trim())}&email=${encodeURIComponent(email.trim())}`,
            {silent: true},
        )
        if (!res.usernameAvailable) throw new Error(res.usernameMessage || '账号不可用')
        if (!res.emailAvailable) throw new Error(res.emailMessage || '邮箱不可用')
    }

    async function sendCode() {
        setError('')
        setStatus('')
        setSending(true)
        try {
            await checkAccount()
            const res = await apiPost<{success: boolean; message?: string}>('/auth/register/code', {
                username: username.trim(),
                email: email.trim(),
            }, {silent: true})
            if (res.success) setStatus(res.message || '验证码已发送')
        } catch (err) {
            setError(err instanceof Error ? err.message : '验证码发送失败')
        } finally {
            setSending(false)
        }
    }

    async function register() {
        setError('')
        setStatus('')
        if (!username.trim() || !email.trim() || !password.trim() || !code.trim()) {
            setError('请完整填写注册信息')
            return
        }
        setRegistering(true)
        try {
            await checkAccount()
            const res = await apiPost<{success: boolean; message?: string}>('/auth/register', {
                username: username.trim(),
                email: email.trim(),
                password,
                code: code.trim(),
            }, {silent: true})
            if (res.success) {
                setStatus(res.message || '注册成功，请登录')
                onRegistered(username.trim())
            }
        } catch (err) {
            setError(err instanceof Error ? err.message : '注册失败')
        } finally {
            setRegistering(false)
        }
    }

    return (
        <div className="fixed inset-0 z-[300] flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
            <div className="w-full max-w-md rounded-card bg-white p-6 shadow-2xl" onClick={(event) => event.stopPropagation()}>
                <div className="mb-5 flex items-start justify-between gap-4">
                    <div>
                        <div className="mb-2 flex items-center gap-2 text-lg font-semibold text-ink">
                            <UserPlus size={20} className="text-accent"/>
                            注册试用账号
                        </div>
                        <p className="text-sm text-muted">试用有效期 {trialDays} 天，验证码将发送到邮箱。</p>
                    </div>
                    <button type="button" className="icon-btn" onClick={onClose}>
                        <X size={16}/>
                    </button>
                </div>

                <div className="space-y-4">
                    <RegisterField icon={<User size={17}/>} label="账号">
                        <input className="input h-10" value={username} onChange={(e) => setUsername(e.target.value)} placeholder="字母开头，3-32 位"/>
                    </RegisterField>
                    <RegisterField icon={<Mail size={17}/>} label="邮箱">
                        <input className="input h-10" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="name@example.com"/>
                    </RegisterField>
                    <RegisterField icon={<Lock size={17}/>} label="密码">
                        <input type="password" className="input h-10" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="至少 6 位"/>
                    </RegisterField>
                    <div>
                        <label className="mb-2 flex items-center gap-2 text-sm text-soft">
                            <ShieldCheck size={17}/>
                            邮箱验证码
                        </label>
                        <div className="flex gap-2">
                            <input className="input h-10" value={code} onChange={(e) => setCode(e.target.value)} placeholder="6 位验证码"/>
                            <button type="button" className="btn-secondary h-10 shrink-0" disabled={sending} onClick={() => void sendCode()}>
                                {sending ? '发送中...' : '发送验证码'}
                            </button>
                        </div>
                    </div>
                </div>

                {error ? <div className="mt-4 rounded-card border border-red-100 bg-red-50 px-3 py-2 text-sm text-red-600">{error}</div> : null}
                {status ? <div className="mt-4 rounded-card border border-green-100 bg-green-50 px-3 py-2 text-sm text-accent">{status}</div> : null}

                <div className="mt-6 flex justify-end gap-2">
                    <button type="button" className="btn-secondary h-10" onClick={onClose}>取消</button>
                    <button type="button" className="btn-primary h-10" disabled={registering} onClick={() => void register()}>
                        {registering ? '注册中...' : '完成注册'}
                    </button>
                </div>
            </div>
        </div>
    )
}

function RegisterField({icon, label, children}: {icon: React.ReactNode; label: string; children: React.ReactNode}) {
    return (
        <label className="block">
            <span className="mb-2 flex items-center gap-2 text-sm text-soft">{icon}{label}</span>
            {children}
        </label>
    )
}
